import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { authServices } from '../authServices'
import { SocialSignIndDTO, SocialSignInPayload } from '../authTypes'

import { ApiErrorCode, ErrorDetail, IOptionsUseMutation } from '@/api/apiTypes'
import { mapAxiosErrorToApiError } from '@/utils'

/**
 * React Query hook for Apple Sign In
 *
 * Sends id_token to the backend for validation
 * Backend endpoint: POST /auth/apple/sign-in
 *
 * @param options - useMutation options (onSuccess, onError)
 * @returns mutation with a function for Apple authentication
 */
export const useSignInWithApple = (
  options?: IOptionsUseMutation<SocialSignIndDTO>,
) => {
  const { t } = useTranslation()

  return useMutation<SocialSignIndDTO, ErrorDetail, SocialSignInPayload>({
    mutationFn: authServices.signInWithApple,
    onSuccess: (data: SocialSignIndDTO) => {
      if (options?.onSuccess) {
        options?.onSuccess(data)
      }
    },
    onError: (err) => {
      const { code } = mapAxiosErrorToApiError(err)
      let errorMessage = t('api:errors.somethingWrong')

      // Handle Apple Auth specific errors
      if (code === ApiErrorCode.ACTION_IS_NOT_ALLOWED) {
        errorMessage = t('api:yourAccountIsDeleted')
      }

      if (code === ApiErrorCode.TOO_MANY_ATTEMPTS) {
        errorMessage = t('api:errors.manyAttempts')
      }

      // Apple-specific errors
      if (code === 'INVALID_APPLE_TOKEN') {
        errorMessage = t('api:errors.invalidAppleToken', {
          defaultValue: 'Invalid Apple authorization token',
        })
      }

      if (code === 'APPLE_USER_NOT_FOUND') {
        errorMessage = t('api:errors.appleUserNotFound', {
          defaultValue: 'Apple user not found',
        })
      }

      console.error('[useSignInWithApple] Error:', { code, errorMessage })

      if (options?.onError) {
        options?.onError({ ...err, errorMessage, code })
      }
    },
  })
}

